import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createHeadquarter,
  updateHeadquarter,
  fetchStates,
  clearErrors,
  clearStatus,
} from "../../slices/headquarter";
import { closeModal } from "../../slices/modals";
import "./_headquarterForm.scss";

// Import local JSON file as fallback
import localStatesData from "../../utilities/state-municipality-areas.json";

const HeadquarterForm = ({ id, open, data, handleClose, title, editTitle }) => {
  const dispatch = useDispatch();
  const { states, createStatus, updateStatus, createError, updateError, statesStatus, statesError } = useSelector(
    (state) => state.headquarter
  );

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phoneNumbers: [""],
    country: "Tunisia",
    state: "",
    delegation: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedStateDelegations, setSelectedStateDelegations] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isEdit = data?.headquarter;

  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    return apiData.map(item => ({
      state: item.Name || item.Value,
      delegations: item.Delegations ? item.Delegations.map(d => d.Name || d.Value) : []
    }));
  };

  const transformLocalData = (localData) => {
    if (!localData || !Array.isArray(localData)) return [];
    
    // Group delegations by their Value (which represents the delegation name)
    const stateMap = new Map();
    
    localData.forEach(item => {
      const stateName = item.Name || item.Value;
      const delegationValue = item.Value;
      
      if (!stateMap.has(stateName)) {
        stateMap.set(stateName, new Set());
      }
      stateMap.get(stateName).add(delegationValue);
    });
    
    return Array.from(stateMap.entries()).map(([state, delegations]) => ({
      state,
      delegations: Array.from(delegations)
    }));
  };

  useEffect(() => {
    if (open) {
      setIsInitialLoad(true);
      
      dispatch(fetchStates()).then((result) => {
        if (result.error) {
          console.log("API failed, using local states data");
          const transformedLocalData = transformLocalData(localStatesData);
          setAvailableStates(transformedLocalData);
        } else {
          const transformedData = transformApiData(result.payload?.data);
          setAvailableStates(transformedData.length > 0 ? transformedData : transformLocalData(localStatesData));
        }
        setIsInitialLoad(false);
      }).catch(() => {
        const transformedLocalData = transformLocalData(localStatesData);
        setAvailableStates(transformedLocalData);
        setIsInitialLoad(false);
      });

      if (isEdit) {
        const headquarter = data.headquarter;
        setFormData({
          name: headquarter.name || "",
          address: headquarter.address || "",
          phoneNumbers: Array.isArray(headquarter.phoneNumbers) ? headquarter.phoneNumbers : [headquarter.phoneNumbers || ""].filter(Boolean),
          country: headquarter.country || "Tunisia",
          state: headquarter.state || "",
          delegation: headquarter.delegation || "",
        });
      } else {
        setFormData({
          name: "",
          address: "",
          phoneNumbers: [""],
          country: "Tunisia",
          state: "",
          delegation: "",
        });
      }
      setErrors({});
    }
  }, [open, isEdit, data]);

  useEffect(() => {
    if (formData.state && availableStates && availableStates.length > 0) {
      const stateData = availableStates.find((s) => s.state === formData.state);
      if (stateData) {
        setSelectedStateDelegations(stateData.delegations);
        
        // Only check for delegation mismatch if we're not in initial load and not in edit mode
        if (!isInitialLoad && (!isEdit || formData.delegation)) {
          const delegationExists = stateData.delegations.some(
            delegation => delegation.toLowerCase() === formData.delegation.toLowerCase()
          );
          
          if (!delegationExists && formData.delegation) {
            console.log('State delegation mismatch - Current:', formData.delegation, 'Available:', stateData.delegations);
            const closeMatch = stateData.delegations.find(
              delegation => delegation.toLowerCase().includes(formData.delegation.toLowerCase()) ||
                           formData.delegation.toLowerCase().includes(delegation.toLowerCase())
            );
            
            if (closeMatch) {
              console.log('Found close match in state change:', closeMatch);
              setFormData(prev => ({ ...prev, delegation: closeMatch }));
            } else {
              console.log('No close match found, clearing delegation');
              setFormData(prev => ({ ...prev, delegation: "" }));
            }
          }
        }
      }
    } else {
      setSelectedStateDelegations([]);
      // Only clear delegation if we're not in edit mode and not in initial load
      if (formData.state && !isEdit && !isInitialLoad) {
        setFormData(prev => ({ ...prev, delegation: "" }));
      }
    }
  }, [formData.state, availableStates, isEdit, isInitialLoad]);

  // Handle delegation matching when editing and states are loaded
  useEffect(() => {
    if (isEdit && availableStates.length > 0 && formData.state && !isInitialLoad) {
      const stateData = availableStates.find((s) => s.state === formData.state);
      if (stateData) {
        console.log('Edit mode - State:', formData.state, 'Delegation:', formData.delegation);
        console.log('Available delegations for state:', stateData.delegations);
        
        // If delegation is empty, try to restore it from the original data
        if (!formData.delegation && data?.headquarter?.delegation) {
          const originalDelegation = data.headquarter.delegation;
          console.log('Restoring original delegation:', originalDelegation);
          
          const delegationExists = stateData.delegations.some(
            delegation => delegation.toLowerCase() === originalDelegation.toLowerCase()
          );
          
          if (delegationExists) {
            setFormData(prev => ({ ...prev, delegation: originalDelegation }));
            console.log('Original delegation restored');
          } else {
            // Try to find a close match for the original delegation
            const closeMatch = stateData.delegations.find(
              delegation => delegation.toLowerCase().includes(originalDelegation.toLowerCase()) ||
                           originalDelegation.toLowerCase().includes(delegation.toLowerCase())
            );
            
            if (closeMatch) {
              console.log('Found close match for original delegation:', closeMatch);
              setFormData(prev => ({ ...prev, delegation: closeMatch }));
            }
          }
        } else if (formData.delegation) {
          // Check if the current delegation exists in the available delegations
          const delegationExists = stateData.delegations.some(
            delegation => delegation.toLowerCase() === formData.delegation.toLowerCase()
          );
          
          if (!delegationExists) {
            console.log('Delegation not found, looking for close match...');
            // Try to find a close match
            const closeMatch = stateData.delegations.find(
              delegation => delegation.toLowerCase().includes(formData.delegation.toLowerCase()) ||
                           formData.delegation.toLowerCase().includes(delegation.toLowerCase())
            );
            
            if (closeMatch) {
              console.log('Found close match:', closeMatch);
              setFormData(prev => ({ ...prev, delegation: closeMatch }));
            } else {
              console.log('No close match found');
            }
          } else {
            console.log('Delegation found and matched');
          }
        }
      }
    }
  }, [isEdit, availableStates, formData.state, formData.delegation, data, isInitialLoad]);

  useEffect(() => {
    if (createStatus === "succeeded") {
      toast.success("Headquarter created successfully!");
      dispatch(clearStatus());
      handleClose(id);
    } else if (createStatus === "failed") {
      toast.error(createError || "Failed to create headquarter");
      dispatch(clearErrors());
    }

    if (updateStatus === "succeeded") {
      toast.success("Headquarter updated successfully!");
      dispatch(clearStatus());
      handleClose(id);
    } else if (updateStatus === "failed") {
      toast.error(updateError || "Failed to update headquarter");
      dispatch(clearErrors());
    }
  }, [createStatus, updateStatus, createError, updateError]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.state) {
      newErrors.state = "State is required";
    }

    if (!formData.delegation) {
      newErrors.delegation = "Delegation is required";
    }

    const phoneNumbersArray = Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""];
    if (!phoneNumbersArray.length || phoneNumbersArray.every(phone => !phone.trim())) {
      newErrors.phoneNumbers = "At least one phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const phoneNumbersArray = Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""];
    const submitData = {
      ...formData,
      phoneNumbers: phoneNumbersArray.filter(phone => phone.trim()),
    };

    if (isEdit) {
      await dispatch(updateHeadquarter({ id: data.headquarter._id, headquarterData: submitData }));
    } else {
      await dispatch(createHeadquarter(submitData));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handlePhoneNumberChange = (index, value) => {
    const phoneNumbersArray = Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""];
    const newPhoneNumbers = [...phoneNumbersArray];
    newPhoneNumbers[index] = value;
    setFormData(prev => ({ ...prev, phoneNumbers: newPhoneNumbers }));
    
    if (errors.phoneNumbers) {
      setErrors(prev => ({ ...prev, phoneNumbers: "" }));
    }
  };

  const addPhoneNumber = () => {
    const phoneNumbersArray = Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""];
    setFormData(prev => ({
      ...prev,
      phoneNumbers: [...phoneNumbersArray, ""]
    }));
  };

  const removePhoneNumber = (index) => {
    const phoneNumbersArray = Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""];
    if (phoneNumbersArray.length > 1) {
      const newPhoneNumbers = phoneNumbersArray.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, phoneNumbers: newPhoneNumbers }));
    }
  };

  return (
    <Dialog open={open} onClose={() => handleClose(id)} maxWidth="md" fullWidth>
      <DialogTitle className="modal-title">
        {isEdit ? editTitle : title}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box className="form-container">
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              error={!!errors.address}
              helperText={errors.address}
              margin="normal"
              required
              multiline
              rows={3}
            />

            <Box className="phone-numbers-section">
              <Typography variant="subtitle1" gutterBottom>
                Phone Numbers *
              </Typography>
              {(Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""]).map((phone, index) => (
                <Box key={index} className="phone-number-row">
                  <TextField
                    fullWidth
                    label={`Phone Number ${index + 1}`}
                    value={phone}
                    onChange={(e) => handlePhoneNumberChange(index, e.target.value)}
                    margin="normal"
                    required={index === 0}
                  />
                  {(Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers : [formData.phoneNumbers || ""]).length > 1 && (
                    <IconButton
                      onClick={() => removePhoneNumber(index)}
                      color="error"
                      className="remove-phone-btn"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={addPhoneNumber}
                variant="outlined"
                className="add-phone-btn"
              >
                Add Phone Number
              </Button>
              {errors.phoneNumbers && (
                <Typography color="error" variant="caption">
                  {errors.phoneNumbers}
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              disabled
              margin="normal"
            />

            <FormControl fullWidth margin="normal" error={!!errors.state}>
              <InputLabel>State *</InputLabel>
              <Select
                value={formData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                label="State *"
              >
                {availableStates && availableStates.length > 0 ? (
                  availableStates.map((state) => (
                    <MenuItem key={state.state} value={state.state}>
                      {state.state}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {statesStatus === "loading" ? "Loading states..." : "No states available"}
                  </MenuItem>
                )}
              </Select>
              {errors.state && (
                <Typography color="error" variant="caption">
                  {errors.state}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth margin="normal" error={!!errors.delegation}>
              <InputLabel>Delegation *</InputLabel>
              <Select
                value={formData.delegation}
                onChange={(e) => handleInputChange("delegation", e.target.value)}
                label="Delegation *"
                disabled={!formData.state}
              >
                {selectedStateDelegations.length > 0 ? (
                  selectedStateDelegations.map((delegation) => (
                    <MenuItem key={delegation} value={delegation}>
                      {delegation}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {formData.state ? "No delegations found" : "Select a state first"}
                  </MenuItem>
                )}
              </Select>
              {errors.delegation && (
                <Typography color="error" variant="caption">
                  {errors.delegation}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(id)} color="secondary">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={createStatus === "loading" || updateStatus === "loading"}
          >
            {createStatus === "loading" || updateStatus === "loading"
              ? "Saving..."
              : isEdit
              ? "Update"
              : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default HeadquarterForm; 